import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

const renderTextWithBold = (text) => {
    if (!text) return "";
    const parts = text.split('**');
    return parts.map((part, index) => {
        if (index % 2 === 1) {
            return <strong key={index} className="font-bold text-slate-800 dark:text-slate-100">{part}</strong>;
        }
        return part;
    });
};

const Hero = () => {
    const [inputValue, setInputValue] = useState("");
    const [activeQuery, setActiveQuery] = useState(null);
    const [answerText, setAnswerText] = useState("");
    const [isTyping, setIsTyping] = useState(false);
    const typingIntervalRef = React.useRef(null);

    React.useEffect(() => {
        return () => {
            if (typingIntervalRef.current) {
                clearInterval(typingIntervalRef.current);
            }
        };
    }, []);

    const qaData = {
        workflow: {
            question: "How do you optimize developer workflows?",
            answer: `### Tier 1: Developer Workflow Optimization
At Jibu Labs, we audit your software engineering lifecycle to identify structural friction points.
* **Custom Coding Agents**: We configure and deploy custom code-generation assistants tailored to your private APIs and coding standards.
* **Automated QA Verification**: We inject agentic regression verification into your CI/CD pipelines.
* **Efficiency Metric**: Our clients typically experience a **2.1x speedup** in overall engineering velocity and release cycles.`
        },
        mobile: {
            question: "What is your mobile AI experience?",
            answer: `### Tier 2: Mobile AI Specialization & GDE Android
Led by a recognized **Google Developer Expert (GDE) for Android**, we bridge generative AI with native mobile engineering.
* **On-Device Inference**: We design and deploy optimized vision models and local vector database systems using Kotlin Multiplatform (KMP).
* **Zero Latency & Privacy**: By running inference locally on the device (CPU/GPU/NPU), we ensure absolute user privacy and offline capabilities.
* **Platform Standards**: Expert integration using Jetpack Compose, SwiftUI, and native neural APIs.`
        },
        smb: {
            question: "How can SMBs automate operations?",
            answer: `### Tier 3: SMB AI Adoption & Automation
We believe you don't need a massive machine learning research team to leverage the power of AI.
* **Process Automation Audits**: We audit your business workflows to replace repetitive tasks (e.g. customer support sorting, CRM entries) with off-the-shelf AI agents.
* **AI Tool Stack Consulting**: We evaluate SaaS offerings to recommend a highly optimized stack, matching pricing and compliance.
* **Prompt Engineering Workshops**: We train your staff to write precise, secure prompts to automate 80%+ of manual overhead.`
        },
        background: {
            question: "What is your executive background?",
            answer: `### Executive Leadership & Strategy
Jibu Labs bridges the gap between deep technical implementation and board-level business strategy.
* **Cornell Executive MBA**: Backed by top-tier business management training, ensuring every AI roadmap aligns directly with operational KPIs and financial ROI.
* **Enterprise Scale & Compliance**: Years of engineering management experience leading core mobile architectures inside Tier 1 financial institutions, ensuring strict compliance and banking-grade security.`
        }
    };

    const handleSearch = (key) => {
        if (!qaData[key]) return;
        
        if (typingIntervalRef.current) {
            clearInterval(typingIntervalRef.current);
        }

        setIsTyping(true);
        setActiveQuery(qaData[key].question);
        setAnswerText("");
        
        let fullText = qaData[key].answer;
        let index = 0;
        
        typingIntervalRef.current = setInterval(() => {
            if (index < fullText.length) {
                const char = fullText[index];
                setAnswerText(prev => prev + char);
                index++;
            } else {
                clearInterval(typingIntervalRef.current);
                typingIntervalRef.current = null;
                setIsTyping(false);
            }
        }, 5);
    };

    const handleCustomSubmit = (e) => {
        e.preventDefault();
        if (!inputValue.trim()) return;
        
        const query = inputValue.toLowerCase();
        let matchedKey = "workflow";
        if (query.includes("mobile") || query.includes("android") || query.includes("gde")) {
            matchedKey = "mobile";
        } else if (query.includes("smb") || query.includes("automate") || query.includes("workshop")) {
            matchedKey = "smb";
        } else if (query.includes("emba") || query.includes("mba") || query.includes("cornell") || query.includes("executive")) {
            matchedKey = "background";
        }
        
        handleSearch(matchedKey);
        setInputValue("");
    };

    return (
        <div className="w-full mx-auto px-6 py-12 lg:px-16 lg:py-24 text-slate-850 dark:text-slate-100 transition-colors duration-300 relative overflow-hidden">
            {/* Ambient Background Gradient Orbs */}
            <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[350px] h-[350px] sm:w-[500px] sm:h-[500px] glow-green opacity-20 dark:opacity-20 blur-[80px] sm:blur-[100px] pointer-events-none -z-10 animate-pulse"></div>
            <div className="absolute top-1/3 left-1/4 w-[200px] h-[200px] sm:w-[300px] sm:h-[300px] glow-indigo opacity-15 dark:opacity-15 blur-[60px] sm:blur-[80px] pointer-events-none -z-10 animate-float"></div>

            <div className="max-w-3xl mx-auto flex flex-col items-center justify-center text-center space-y-8">
                {/* Heading */}
                <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    className="space-y-4"
                >
                    {/* Chase Ink Award Badge */}
                    <div className="inline-flex items-center space-x-2 bg-emerald-100/50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/20 px-4 py-1.5 rounded-full text-xs font-semibold text-forest dark:text-emerald-400 select-none animate-float shadow-sm mb-2">
                        <svg className="w-4 h-4 text-emerald-500 animate-spin-slow" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                        </svg>
                        <span>Awarded Top 100 Entreprenista Award by Chase Ink</span>
                    </div>

                    <h1 className="text-4xl lg:text-6xl font-bold tracking-tight bg-gradient-to-r from-forest via-emerald-650 to-teal-500 dark:from-white dark:via-slate-100 dark:to-emerald-300 bg-clip-text text-transparent leading-tight logo-font">
                        Operationalizing AI.
                    </h1>
                    <p className="text-sm lg:text-base text-slate-500 dark:text-slate-400 font-light max-w-xl mx-auto leading-relaxed">
                        Ask Jibu Labs about Enterprise AI, Mobile ML Integration, SMB Automation, or our Executive credentials.
                    </p>
                </motion.div>

                {/* Perplexity-style Input Box */}
                <motion.form 
                    onSubmit={handleCustomSubmit}
                    className="w-full relative rounded-2xl border border-slate-200 dark:border-slate-800/80 bg-white dark:bg-[#202222] shadow-[0_4px_25px_rgba(0,0,0,0.03)] dark:shadow-[0_4px_30px_rgba(0,0,0,0.3)] focus-within:ring-2 focus-within:ring-emerald-500/15 focus-within:border-emerald-500/40 p-2.5 transition-all duration-300"
                    initial={{ opacity: 0, scale: 0.98 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.5, delay: 0.2 }}
                >
                    <div className="flex items-center space-x-3 px-3">
                        <svg className="w-5 h-5 text-slate-400 dark:text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                        </svg>
                        <input 
                            type="text" 
                            value={inputValue}
                            onChange={(e) => setInputValue(e.target.value)}
                            placeholder="Ask Jibu Labs anything..."
                            className="w-full bg-transparent border-none focus:outline-none focus:ring-0 text-sm py-2 text-slate-800 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500"
                        />
                        <button 
                            type="submit"
                            className="p-2 rounded-xl bg-forest hover:bg-forest-hover text-white transition-all shadow-[0_0_12px_rgba(16,185,129,0.35)]"
                        >
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                            </svg>
                        </button>
                    </div>
                </motion.form>

                {/* Suggestions / Chips */}
                <motion.div 
                    className="flex flex-wrap items-center justify-center gap-2.5 pt-2"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.5, delay: 0.4 }}
                >
                    <motion.button 
                        type="button"
                        onClick={() => handleSearch("workflow")}
                        whileHover={{ scale: 1.04, y: -1 }}
                        whileTap={{ scale: 0.98 }}
                        className="px-4 py-2 rounded-full text-xs border border-slate-200 dark:border-slate-800 hover:border-emerald-500/30 bg-slate-50 dark:bg-[#202222] hover:bg-emerald-500/5 text-slate-650 dark:text-slate-400 hover:text-forest dark:hover:text-emerald-400 transition-all font-medium shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
                    >
                        Optimize dev workflows
                    </motion.button>
                    <motion.button 
                        type="button"
                        onClick={() => handleSearch("mobile")}
                        whileHover={{ scale: 1.04, y: -1 }}
                        whileTap={{ scale: 0.98 }}
                        className="px-4 py-2 rounded-full text-xs border border-slate-200 dark:border-slate-800 hover:border-emerald-500/30 bg-slate-50 dark:bg-[#202222] hover:bg-emerald-500/5 text-slate-650 dark:text-slate-400 hover:text-forest dark:hover:text-emerald-400 transition-all font-medium shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
                    >
                        Mobile AI Specialization
                    </motion.button>
                    <motion.button 
                        type="button"
                        onClick={() => handleSearch("smb")}
                        whileHover={{ scale: 1.04, y: -1 }}
                        whileTap={{ scale: 0.98 }}
                        className="px-4 py-2 rounded-full text-xs border border-slate-200 dark:border-slate-800 hover:border-emerald-500/30 bg-slate-50 dark:bg-[#202222] hover:bg-emerald-500/5 text-slate-650 dark:text-slate-400 hover:text-forest dark:hover:text-emerald-400 transition-all font-medium shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
                    >
                        SMB AI Automation
                    </motion.button>
                    <motion.button 
                        type="button"
                        onClick={() => handleSearch("background")}
                        whileHover={{ scale: 1.04, y: -1 }}
                        whileTap={{ scale: 0.98 }}
                        className="px-4 py-2 rounded-full text-xs border border-slate-200 dark:border-slate-800 hover:border-emerald-500/30 bg-slate-50 dark:bg-[#202222] hover:bg-emerald-500/5 text-slate-650 dark:text-slate-400 hover:text-forest dark:hover:text-emerald-400 transition-all font-medium shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
                    >
                        Executive credentials
                    </motion.button>
                </motion.div>

                {/* Conversational Answer Area */}
                <AnimatePresence mode="wait">
                    {activeQuery && (
                        <motion.div 
                            className="w-full text-left p-6 lg:p-8 rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-[#202222] shadow-[0_4px_30px_rgba(0,0,0,0.03)] dark:shadow-[0_10px_40px_rgba(0,0,0,0.2)] mt-8 space-y-4 transition-colors duration-300"
                            initial={{ opacity: 0, y: 15 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -15 }}
                            transition={{ duration: 0.4 }}
                        >
                            {/* Question Thread */}
                            <div className="flex items-start space-x-3 border-b border-slate-100 dark:border-slate-800/80 pb-4">
                                <span className="p-2 rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-500">
                                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                    </svg>
                                </span>
                                <div>
                                    <h4 className="text-[10px] font-semibold text-slate-450 uppercase tracking-wider">Query</h4>
                                    <p className="text-base font-semibold text-slate-800 dark:text-slate-100 mt-0.5">{activeQuery}</p>
                                </div>
                            </div>

                            {/* Answer Response Thread */}
                            <div className="flex items-start space-x-3 pt-2">
                                <span className="p-2 rounded-lg bg-emerald-500/10 text-emerald-600 dark:text-emerald-400">
                                    <svg className="w-4 h-4 animate-pulse" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M9.663 17h4.673M12 3v1m6.364.364l-.707.707M21 12h-1M4 12H3m3.343-5.657l.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                                    </svg>
                                </span>
                                <div className="flex-grow">
                                    <div className="flex justify-between items-center">
                                        <h4 className="text-[10px] font-semibold text-slate-450 uppercase tracking-wider">Jibu Labs AI</h4>
                                        {isTyping && (
                                            <span className="text-[10px] text-emerald-500 dark:text-emerald-400 animate-pulse font-mono font-semibold">TYPING...</span>
                                        )}
                                    </div>
                                    
                                    <div className="text-sm text-slate-600 dark:text-slate-350 mt-3 space-y-4 leading-relaxed font-light font-roboto">
                                        {answerText.split('\n').map((line, idx) => {
                                            if (line.startsWith('### ')) {
                                                const content = line.replace('### ', '');
                                                return (
                                                    <h3 key={idx} className="text-lg font-bold text-slate-800 dark:text-slate-100 pt-2">
                                                        {renderTextWithBold(content)}
                                                    </h3>
                                                );
                                            } else if (line.startsWith('## ')) {
                                                const content = line.replace('## ', '');
                                                return (
                                                    <h2 key={idx} className="text-base font-bold text-slate-800 dark:text-slate-100 pt-2">
                                                        {renderTextWithBold(content)}
                                                    </h2>
                                                );
                                            } else if (line.startsWith('* ')) {
                                                const cleanItem = line.replace('* ', '');
                                                const parts = cleanItem.split(': ');
                                                if (parts.length > 1) {
                                                    return (
                                                        <li key={idx} className="list-disc list-inside ml-2">
                                                            <span className="font-bold text-slate-700 dark:text-slate-200">{renderTextWithBold(parts[0])}</span>: {renderTextWithBold(parts.slice(1).join(': '))}
                                                        </li>
                                                    );
                                                }
                                                return <li key={idx} className="list-disc list-inside ml-2">{renderTextWithBold(cleanItem)}</li>;
                                            } else if (line.trim() === '') {
                                                return <div key={idx} className="h-2"></div>;
                                            }
                                            return <p key={idx}>{renderTextWithBold(line)}</p>;
                                        })}
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </div>
    );
};

export default Hero;